import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { CurrencyDollar, Bank, CreditCard, Warning, CaretRight, FileX, Tray, Sparkle, CircleNotch } from "@phosphor-icons/react";

const money = (n) => `$${(n || 0).toLocaleString()}`;

export function BillingMetrics({ b }) {
  if (!b) return null;
  return (
    <div className="space-y-4" data-testid="billing-metrics">
      <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Real-time billings</p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="stat-invoiced">
          <div className="flex items-center gap-2 text-zinc-500 mb-2"><CurrencyDollar size={15} /><span className="text-xs">Fees invoiced</span></div>
          <p className="font-mono text-2xl text-zinc-900">{money(b.invoiced_mtd)} <span className="text-xs text-zinc-400">MTD</span></p>
          <p className="font-mono text-xs text-zinc-500 mt-1">{money(b.invoiced_ytd)} YTD</p>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="stat-received">
          <div className="flex items-center gap-2 text-zinc-500 mb-2"><Bank size={15} /><span className="text-xs">Fees received</span></div>
          <p className="font-mono text-2xl text-zinc-900">{money(b.received_mtd)} <span className="text-xs text-zinc-400">MTD</span></p>
          <p className="font-mono text-xs text-zinc-500 mt-1">{money(b.received_ytd)} YTD</p>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="stat-settlement">
          <div className="flex items-center gap-2 text-zinc-500 mb-2"><CreditCard size={15} /><span className="text-xs">Settlement mix</span></div>
          <p className="font-mono text-sm text-zinc-900">ACH {money(b.received_by_method?.ach)}</p>
          <p className="font-mono text-sm text-zinc-900 mt-1">Card {money(b.received_by_method?.card)}</p>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="stat-ar">
          <div className="flex items-center gap-2 text-zinc-500 mb-2"><Warning size={15} /><span className="text-xs">Outstanding A/R</span></div>
          <p className="font-mono text-2xl text-rose-700">{money(b.ar_total)}</p>
        </div>
      </div>
      <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="ar-aging">
        <h3 className="text-sm font-medium text-zinc-900 mb-3">A/R aging buckets</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {["0-30", "31-60", "61-90", "90+"].map((k) => (
            <div key={k} className={`rounded-md border p-3 ${k === "90+" ? "border-rose-200 bg-rose-50/50" : "border-zinc-200"}`}>
              <p className="text-xs text-zinc-500 mb-1">{k} days</p>
              <p className="font-mono text-lg text-zinc-900">{money(b.ar_aging?.[k]?.total)}</p>
              <div className="mt-2 space-y-1">
                {(b.ar_aging?.[k]?.items || []).map((i) => (
                  <Link key={i.number} to={`/clients/${i.client_id}`} data-testid={`ar-item-${i.number}`} className="flex justify-between text-xs text-zinc-600 hover:text-zinc-900 transition-colors duration-150">
                    <span className="truncate mr-2">{i.client_name}</span><span className="font-mono shrink-0">{money(i.amount)}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProductionPipeline({ p }) {
  if (!p) return null;
  const queues = [
    { label: "Intake / Organizers pending", val: p.bottlenecks?.organizers_pending },
    { label: "Assigned to prep", val: p.queues?.assigned_to_prep },
    { label: "Staff review pending", val: p.queues?.staff_review_pending },
    { label: "CPA / Owner review pending", val: p.queues?.cpa_review_pending, hot: true },
  ];
  return (
    <div className="space-y-4" data-testid="production-pipeline">
      <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Tax lifecycle & production pipeline</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <h3 className="text-sm font-medium text-zinc-900 mb-1">Returns in system</h3>
          <p className="font-mono text-3xl text-zinc-900 mb-3">{p.total_returns}</p>
          <div className="space-y-1.5">
            {Object.entries(p.by_form_type || {}).map(([f, n]) => (
              <div key={f} className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs text-zinc-600">Form {f}</span>
                <span className="font-mono text-xs text-zinc-900 bg-zinc-100 border border-zinc-200 rounded-sm px-1.5">{n}</span>
              </div>
            ))}
          </div>
          <Link to="/documents" className="flex items-center gap-1 text-xs text-amber-700 mt-3 hover:underline" data-testid="bottleneck-link">
            <FileX size={13} /> {p.bottlenecks?.missing_doc_clients} clients with missing-doc alerts <CaretRight size={10} />
          </Link>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <h3 className="text-sm font-medium text-zinc-900 mb-3">Production queue</h3>
          <div className="space-y-2">
            {queues.map((q) => (
              <Link key={q.label} to="/pipeline" className={`flex items-center justify-between rounded-md border px-3 py-2 transition-colors duration-150 hover:bg-zinc-50 ${q.hot ? "border-amber-200 bg-amber-50/40" : "border-zinc-200"}`}>
                <span className="text-xs text-zinc-700">{q.label}</span>
                <span className="font-mono text-sm text-zinc-900">{q.val ?? 0}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="efile-command-center">
          <h3 className="text-sm font-medium text-zinc-900 mb-3">E-file command center</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2">
              <span className="text-xs text-zinc-700">Awaiting client 8879 signature</span>
              <span className="font-mono text-sm text-zinc-900">{p.efile?.awaiting_8879 ?? 0}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2">
              <span className="text-xs text-zinc-700">Pending IRS / state acceptance</span>
              <span className="font-mono text-sm text-zinc-900">{p.efile?.pending_irs ?? 0}</span>
            </div>
            <div className="rounded-md border border-rose-200 bg-rose-50/50 px-3 py-2" data-testid="rejections-queue">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-rose-700 flex items-center gap-1"><Warning size={13} weight="fill" /> Rejections queue</span>
                <span className="font-mono text-sm text-rose-700">{p.efile?.rejections?.length ?? 0}</span>
              </div>
              {(p.efile?.rejections || []).map((r) => (
                <Link key={r.engagement_id} to={`/clients/${r.client_id}`} className="block mt-2 text-xs text-rose-800 hover:underline">
                  {r.client_name} — {r.service_type} {r.tax_year}
                  <span className="block text-[10px] text-rose-600 font-mono">{r.reason}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DailyBriefing({ meetings }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="daily-briefing">
      <h3 className="text-sm font-medium text-zinc-900 mb-3">Today's schedule & AI briefing</h3>
      <div className="space-y-3">
        {(meetings || []).map((m) => (
          <div key={m.id} className="border-b border-zinc-100 last:border-0 pb-3 last:pb-0" data-testid={`meeting-${m.id}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-zinc-500 w-12 shrink-0">{m.time}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-900 truncate">{m.title}</p>
                <p className="text-[10px] text-zinc-400">{m.kind}</p>
              </div>
              {m.status === "no_show" && <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-sm px-1.5 py-0.5">NO-SHOW</span>}
              {m.status === "rescheduled" && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1.5 py-0.5">RESCHEDULED</span>}
            </div>
            <p className="mt-1.5 ml-[60px] text-xs text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md px-2.5 py-1.5">{m.ai_context}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CommsCenter({ comms }) {
  const qc = useQueryClient();
  const draft = useMutation({
    mutationFn: (id) => api.post(`/notices/${id}/draft`),
    onSuccess: () => { toast.success("Response drafted — waiting in the CPA approval queue"); qc.invalidateQueries(); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="comms-center">
      <h3 className="text-sm font-medium text-zinc-900 mb-3">Cognitive communication center</h3>
      <Link to="/approvals" data-testid="ai-drafts-link" className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2 mb-3 hover:bg-zinc-50 transition-colors duration-150">
        <span className="text-xs text-zinc-700 flex items-center gap-1.5"><Tray size={14} /> AI drafts awaiting approval</span>
        <span className="font-mono text-sm text-zinc-900">{comms?.ai_drafts_pending ?? 0}</span>
      </Link>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Urgent inbox (AI-filtered)</p>
      <div className="space-y-1.5 mb-3">
        {(comms?.urgent_inbox || []).map((m) => (
          <Link key={m.id} to={`/clients/${m.client_id}`} className="block rounded-md border border-amber-200 bg-amber-50/40 px-2.5 py-1.5 text-xs text-zinc-700 hover:bg-amber-50">
            <span className="font-medium">{m.sender_name}:</span> {m.body.slice(0, 90)}…
          </Link>
        ))}
        {!comms?.urgent_inbox?.length && <p className="text-xs text-zinc-400 italic">No urgent client messages.</p>}
      </div>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">IRS / Comptroller notice monitor</p>
      <div className="space-y-1.5">
        {(comms?.notices || []).map((n) => (
          <div key={n.id} className={`rounded-md border px-2.5 py-2 ${n.priority === "critical" ? "border-rose-200 bg-rose-50/50" : "border-zinc-200"}`} data-testid={`notice-${n.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-900">{n.agency} {n.notice_type} — {n.client_name}</span>
              <span className={`text-[10px] uppercase ${n.priority === "critical" ? "text-rose-700" : "text-amber-700"}`}>{n.priority}</span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-0.5">{n.summary}</p>
            <div className="mt-1.5">
              {n.draft_action_id ? (
                <Link to="/approvals" data-testid={`notice-draft-link-${n.id}`} className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5 hover:bg-emerald-100 transition-colors duration-150">
                  <Sparkle size={10} /> Draft in approval queue →
                </Link>
              ) : (
                <button data-testid={`notice-draft-btn-${n.id}`} disabled={draft.isPending}
                  onClick={() => draft.mutate(n.id)}
                  className="inline-flex items-center gap-1 text-[10px] text-zinc-700 border border-zinc-300 rounded-sm px-1.5 py-0.5 hover:bg-zinc-100 transition-colors duration-150">
                  {draft.isPending && draft.variables === n.id ? <CircleNotch size={10} className="animate-spin" /> : <Sparkle size={10} />} Draft response with AI
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TaskManager({ tasks, onToggle }) {
  const groups = [
    { key: "critical", label: "Critical — filing deadlines / statutory", cls: "text-rose-700 bg-rose-50 border-rose-200" },
    { key: "high", label: "High — advisory & structural work", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    { key: "medium", label: "Medium — internal operations", cls: "text-zinc-600 bg-zinc-100 border-zinc-200" },
  ];
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="smart-task-manager">
      <h3 className="text-sm font-medium text-zinc-900 mb-3">Smart task manager</h3>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key}>
            <span className={`text-[10px] uppercase tracking-wide rounded-sm border px-1.5 py-0.5 ${g.cls}`}>{g.label}</span>
            <div className="mt-1.5 space-y-1">
              {(tasks?.[g.key] || []).map((t) => (
                <label key={t.id} className="flex items-start gap-2 py-0.5 cursor-pointer" data-testid={`cc-task-${t.id}`}>
                  <input type="checkbox" className="mt-1" onChange={() => onToggle(t.id)} />
                  <span className="text-xs text-zinc-700">{t.title} <span className="text-zinc-400">· {t.client_name} · due <span className="font-mono">{t.due_date}</span></span></span>
                </label>
              ))}
              {!(tasks?.[g.key] || []).length && <p className="text-xs text-zinc-300 italic">None</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
